/* Product enquiries stay on the page until the visitor chooses a contact channel. */
(() => {
  if (customElements.get('yatdim-contact')) return;

  class YatdimContact extends HTMLElement {
    #controller;
    #template;
    #destination;
    #selected;
    #status;
    #product;
    #renderedValue = '';
    #announcementFrame;

    connectedCallback() {
      this.#controller?.abort();
      this.#controller = new AbortController();
      const { signal } = this.#controller;
      this.#template = this.querySelector('[data-yd-template]');
      this.#destination = this.querySelector('[data-yd-destination]');
      this.#selected = this.querySelector('[data-yd-selected-product]');
      this.#status = this.querySelector('[data-yd-status]');
      if (!this.#template) return;

      for (const button of this.querySelectorAll('[data-yd-copy]')) button.hidden = false;
      if (!this.#renderedValue) this.#renderedValue = this.#template.value;
      this.addEventListener('click', this.#onClick, { signal });
      this.#destination?.addEventListener('input', this.#onDestination, { signal });
      this.#template.addEventListener('input', this.#onTemplateInput, { signal });
      document.addEventListener('yatdim:product-selected', this.#onProductSelected, { signal });
      this.#syncLinks();
    }

    disconnectedCallback() {
      this.#controller?.abort();
      cancelAnimationFrame(this.#announcementFrame);
    }

    #onDestination = () => {
      // Preserve a visitor's hand-written enquiry when changing their destination.
      if (this.#template.value === this.#renderedValue) this.#renderTemplate();
      this.#syncLinks();
    };

    #onTemplateInput = () => this.#syncLinks();

    #onProductSelected = (event) => {
      if (!this.id || event.detail?.contactId !== this.id) return;
      const selected = event.detail?.product;
      const title = this.#plainText(selected?.title, 300);
      if (!title) return;
      event.preventDefault();
      let productUrl = '';
      try {
        const url = new URL(this.#plainText(selected.url, 2000), location.href);
        if (selected.url && ['https:', 'http:'].includes(url.protocol)) productUrl = url.href;
      } catch { /* A missing or malformed product URL is omitted from the enquiry. */ }
      const variant = this.#plainText(selected.variantTitle, 200);
      this.#product = {
        title,
        variant: variant === 'Default Title' ? '' : variant,
        sku: this.#plainText(selected.sku, 100),
        url: productUrl
      };
      if (this.#selected) {
        this.#selected.textContent = [this.#product.title, this.#product.variant].filter(Boolean).join(' · ');
      }
      this.#renderTemplate();
      this.#syncLinks();
      this.#announce(this.dataset.productSelected);
      const focusTarget = this.querySelector('[data-yd-product-focus]');
      focusTarget?.focus({ preventScroll: true });
      focusTarget?.scrollIntoView({
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        block: 'center'
      });
    };

    /** @param {unknown} value @param {number} limit @returns {string} */
    #plainText(value, limit) {
      return ['string', 'number'].includes(typeof value) ? String(value).trim().slice(0, limit) : '';
    }

    #renderTemplate() {
      const substitutions = {
        product: this.#product?.title || this.dataset.productFallback || '',
        variant: this.#product?.variant || this.dataset.unspecified || '',
        sku: this.#product?.sku || this.dataset.unspecified || '',
        url: this.#product?.url || '',
        destination: this.#destination?.value.trim() || this.dataset.destinationFallback || ''
      };
      this.#renderedValue = (this.dataset.template || '').replace(
        /\{(product|variant|sku|url|destination)\}/g,
        (_, key) => substitutions[key]
      );
      this.#template.value = this.#renderedValue;
    }

    #syncLinks() {
      const message = this.#template.value;
      for (const link of this.querySelectorAll('[data-yd-email-link], [data-yd-whatsapp-link]')) {
        try {
          const url = new URL(link.getAttribute('href'), location.href);
          if (link.hasAttribute('data-yd-email-link') && url.protocol === 'mailto:') {
            url.searchParams.set('subject', this.dataset.subject || '');
            url.searchParams.set('body', message);
            link.href = url.href;
          }
          if (link.hasAttribute('data-yd-whatsapp-link') && url.protocol === 'https:' && ['wa.me', 'api.whatsapp.com', 'web.whatsapp.com'].includes(url.hostname)) {
            url.searchParams.set('text', message);
            link.href = url.href;
          }
        } catch { /* Keep the configured link if it cannot be enhanced. */ }
      }
    }

    #onClick = async (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-yd-copy]');
      if (!button || !this.contains(button)) return;
      const isTemplate = button.dataset.ydCopy === 'template';
      const text = isTemplate ? this.#template.value : button.dataset.copyValue;
      if (!text) return;
      const controller = this.#controller;
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard is unavailable');
        await navigator.clipboard.writeText(text);
        if (this.isConnected && controller === this.#controller) this.#announce(this.dataset.copySuccess);
      } catch {
        if (!this.isConnected || controller !== this.#controller) return;
        if (isTemplate) {
          this.#template.focus();
          this.#template.select();
        } else {
          const value = button.closest('.yd-ds-contact__channel')?.querySelector('[data-yd-channel-value]');
          if (value) {
            value.focus();
            const selection = getSelection();
            const range = document.createRange();
            range.selectNodeContents(value);
            selection?.removeAllRanges();
            selection?.addRange(range);
          }
        }
        this.#announce(this.dataset.copyFailure);
      }
    };

    #announce(message) {
      if (!this.#status) return;
      cancelAnimationFrame(this.#announcementFrame);
      this.#status.textContent = '';
      this.#announcementFrame = requestAnimationFrame(() => {
        if (this.isConnected) this.#status.textContent = message || '';
      });
    }
  }

  customElements.define('yatdim-contact', YatdimContact);
})();
